import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { DarkPortalShell, AttachmentList, NewBadge } from "../components/Shell";
import { useNewFlags } from "../hooks/useNewFlags";
import DocViewer from "../components/DocViewer";
import VideoModal from "../components/VideoModal";
import { DocSearchBar, filterDocs } from "../components/DocSearchBar";
import { FileText, Eye, Youtube, MessageSquare, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const INPUT_STYLE = { backgroundColor: "#0A1F44", border: "1px solid #1E3A8A", color: "#fff" };

const FEEDBACK_INTRO =
  "This is where you can submit any feedback, thoughts, or questions as you read. You will not receive a reply through the portal. All responses and answers will come via your manager and/or toolbox talks.";

function validateFeedback(mode, docId, text) {
  if (!text.trim()) return "Please enter your feedback or question";
  if (mode === "document" && !docId) return "Please choose a document";
  return null;
}

function buildFeedbackFormData(mode, docId, text, name, anonymous, documents) {
  const doc = documents.find((d) => d.id === docId);
  const fd = new FormData();
  fd.append("feedback_text", text.trim());
  fd.append("anonymous", anonymous ? "true" : "false");
  if (!anonymous && name.trim()) fd.append("name", name.trim());
  fd.append("document_title", mode === "document" && doc ? doc.title : "General Feedback");
  if (mode === "document" && docId) fd.append("document_id", docId);
  return fd;
}

function FeedbackAboutFields({ mode, setMode, docId, setDocId, documents }) {
  return (
    <>
      <div>
        <label className="block text-sm text-[#9CA3AF] mb-1.5">What's this about?</label>
        <select data-testid="feedback-category-select" value={mode} onChange={(e) => setMode(e.target.value)}
          className="w-full rounded-lg px-3 py-2.5 outline-none" style={INPUT_STYLE}>
          <option value="general">General feedback</option>
          <option value="document">About a specific document</option>
        </select>
      </div>
      {mode === "document" && (
        <div>
          <label className="block text-sm text-[#9CA3AF] mb-1.5">Which document?</label>
          <select data-testid="feedback-document-select" value={docId} onChange={(e) => setDocId(e.target.value)}
            className="w-full rounded-lg px-3 py-2.5 outline-none" style={INPUT_STYLE}>
            <option value="">Select a document...</option>
            {documents.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        </div>
      )}
    </>
  );
}

function FeedbackIdentityFields({ name, setName, anonymous, setAnonymous }) {
  return (
    <div>
      <label className="block text-sm text-[#9CA3AF] mb-1.5">Your name (optional)</label>
      <input data-testid="feedback-name-input" value={name} disabled={anonymous}
        onChange={(e) => setName(e.target.value)} placeholder="Enter your name"
        className="w-full rounded-lg px-3 py-2.5 outline-none disabled:opacity-50" style={INPUT_STYLE} />
      <label className="flex items-center gap-2 mt-2.5 text-sm text-[#cbd5e1] cursor-pointer w-fit">
        <input type="checkbox" data-testid="feedback-anonymous-toggle" checked={anonymous}
          onChange={(e) => setAnonymous(e.target.checked)} className="accent-[#F97316] w-4 h-4" />
        Submit anonymously
      </label>
    </div>
  );
}

function FeedbackForm({ clientId, documents, onDone }) {
  const [mode, setMode] = useState("general");
  const [docId, setDocId] = useState("");
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const error = validateFeedback(mode, docId, text);
    if (error) { toast.error(error); return; }
    setBusy(true);
    try {
      await api.post(`/clients/${clientId}/feedback`, buildFeedbackFormData(mode, docId, text, name, anonymous, documents));
      toast.success("Thanks — your feedback has been sent");
      setText(""); setDocId(""); setMode("general"); setName(""); setAnonymous(false);
      onDone();
    } catch {
      toast.error("Could not send your feedback");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl p-6 mb-6 border" style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }} data-testid="feedback-form">
      <p className="text-sm text-[#cbd5e1] leading-relaxed mb-5" data-testid="feedback-intro">{FEEDBACK_INTRO}</p>
      <div className="space-y-4">
        <FeedbackAboutFields mode={mode} setMode={setMode} docId={docId} setDocId={setDocId} documents={documents} />
        <div>
          <label className="block text-sm text-[#9CA3AF] mb-1.5">Your feedback or question</label>
          <textarea data-testid="feedback-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={4}
            placeholder="Share your feedback, thoughts, or questions..." className="w-full rounded-lg px-3 py-2.5 outline-none resize-y" style={INPUT_STYLE} />
        </div>
        <FeedbackIdentityFields name={name} setName={setName} anonymous={anonymous} setAnonymous={setAnonymous} />
        <button data-testid="feedback-submit-button" onClick={submit} disabled={busy}
          className="flex items-center gap-2 rounded-lg px-5 py-2.5 font-semibold text-white text-sm disabled:opacity-60"
          style={{ backgroundColor: "#F97316" }}>
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send Feedback
        </button>
      </div>
    </div>
  );
}

function WorkerDocCard({ d, onView, onWatch }) {
  const { isNewForMe, markSeen } = useNewFlags();
  const view = (v) => { markSeen(d); onView({ ...v, allowDownload: d.downloadable }); };
  return (
    <div data-testid={`worker-doc-${d.id}`} className="rounded-xl p-5 border"
      style={{ backgroundColor: "#112A57", borderColor: "#1E3A8A" }}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: "rgba(249,115,22,0.15)" }}>
            <FileText size={18} style={{ color: "#F97316" }} />
          </div>
          <p className="text-white font-medium truncate">{d.title}</p>
          {isNewForMe(d) && <NewBadge testId={`worker-new-${d.id}`} />}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {d.main_file && (
            <button data-testid={`worker-view-main-${d.id}`}
              onClick={() => view({ file: d.main_file, title: d.title })}
              className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg text-white transition-colors"
              style={{ backgroundColor: "#F97316" }}>
              <Eye size={15} /> View Document
            </button>
          )}
          {d.video_url && (
            <button data-testid={`worker-watch-video-${d.id}`}
              onClick={() => { markSeen(d); onWatch({ url: d.video_url, title: d.title }); }}
              className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg text-white transition-colors border"
              style={{ borderColor: "#1E3A8A" }}>
              <Youtube size={15} style={{ color: "#F97316" }} /> Watch Video
            </button>
          )}
        </div>
      </div>
      {(d.attachments || []).some((a) => !a.portals || a.portals.includes("team")) && (
        <div className="mt-4"><AttachmentList attachments={(d.attachments || []).filter((a) => !a.portals || a.portals.includes("team"))} onView={view} /></div>
      )}
    </div>
  );
}

function WorkerDocList({ docs, search, setSearch, filterStatus, setFilterStatus, onView, onWatch }) {
  const visibleDocs = filterDocs(docs, search, "all", filterStatus);
  if (docs.length === 0) {
    return (
      <div className="text-center py-20 text-[#9CA3AF]" data-testid="worker-empty">
        <FileText size={40} className="mx-auto mb-3 opacity-40" />
        No documents available yet.
      </div>
    );
  }
  return (
    <>
      <DocSearchBar search={search} setSearch={setSearch} status={filterStatus} setStatus={setFilterStatus} dark testPrefix="worker-doc" />
      {visibleDocs.length === 0 ? (
        <div className="text-center py-16 text-[#9CA3AF]" data-testid="worker-doc-no-match">No documents match your search.</div>
      ) : (
        <div className="space-y-3">
          {visibleDocs.map((d) => <WorkerDocCard key={d.id} d={d} onView={onView} onWatch={onWatch} />)}
        </div>
      )}
    </>
  );
}

function WorkerHeader({ readOnly, onFeedback, newCount }) {
  return (
    <div className="flex items-center justify-between mb-6">
      <h1 className="font-heading text-white text-3xl font-bold flex items-center gap-2.5">
        Documents
        {newCount > 0 && (
          <span data-testid="worker-new-count" title={`${newCount} document(s) marked NEW! for you`}
            className="min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-bold flex items-center justify-center"
            style={{ backgroundColor: "#7C3AED", color: "#fff" }}>{newCount}</span>
        )}
      </h1>
      {!readOnly && (
        <button data-testid="worker-feedback-button" onClick={onFeedback}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          style={{ backgroundColor: "rgba(249,115,22,0.15)", color: "#F97316" }}>
          <MessageSquare size={16} /> Feedback
        </button>
      )}
    </div>
  );
}

export function WorkerView({ clientId, readOnly = false }) {
  const { isNewForMe } = useNewFlags();
  const [docs, setDocs] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [watch, setWatch] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    api.get(`/clients/${clientId}/documents`).then(({ data }) => setDocs(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Server filters to "In Consultation" (excl. Client uploads) for worker sessions; admin read-only view mirrors that here.
  const consultDocs = readOnly
    ? docs.filter((d) => d.status === "In Consultation" && d.category !== "Client uploads")
    : docs;
  const withFiles = consultDocs.filter((d) => d.main_file || d.video_url || (d.attachments && d.attachments.length));

  return (
    <>
      <div className="max-w-3xl mx-auto">
        <WorkerHeader readOnly={readOnly} onFeedback={() => setShowFeedback((s) => !s)}
          newCount={withFiles.filter(isNewForMe).length} />
        {!readOnly && showFeedback && (
          <FeedbackForm clientId={clientId} documents={withFiles} onDone={() => setShowFeedback(false)} />
        )}
        <WorkerDocList docs={withFiles} search={search} setSearch={setSearch}
          filterStatus={filterStatus} setFilterStatus={setFilterStatus} onView={setViewing} onWatch={setWatch} />
      </div>
      {viewing && <DocViewer file={viewing.file} title={viewing.title} allowDownload={!!viewing.allowDownload} onClose={() => setViewing(null)} />}
      {watch && <VideoModal url={watch.url} title={watch.title} onClose={() => setWatch(null)} />}
    </>
  );
}

export default function WorkerPortal() {
  const { auth } = useAuth();
  return (
    <DarkPortalShell subtitle="Team Portal">
      <WorkerView clientId={auth.client_id} />
    </DarkPortalShell>
  );
}
